import React, { useState, useEffect } from 'react';
import { Box, TextField, Button, Typography, Divider } from '@mui/material';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import JobIcon from '../CustomSVGIcons/JobIcon';
import CampaignIcon from '../CustomSVGIcons/CampaignIcon';
import CalendarColoredIcon from '../CustomSVGIcons/CalendarColoredIcon';
import { ThspianAvatar } from '../Avatar/ThspianAvatar';
import { IUserProfile } from '../../redux/user/actions';
import { DetailsMenu } from './details.data';
import { E_FEATURE_FLAGS, useFeatureFlag } from '../../hook/useFeatureFlag';

interface PostInputProps {
    onOpenModal: () => void;
    onClick?: () => void;
    onButtonClick?: (category: keyof DetailsMenu) => void;
    user?: IUserProfile;
}

const PostInput: React.FC<PostInputProps> = ({ onOpenModal, user, onButtonClick }) => {
    const { themeStyle } = useThemeContext();
    const [currentIndex, setCurrentIndex] = useState(0);
    const [events] = useFeatureFlag([E_FEATURE_FLAGS.EVENTS]);
    const placeholders = [
        'Share a thought...',
        'Create a campaign...',
        'Create a job...',
        ...(events ? ['Create an event...'] : []),
    ];

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentIndex(prevIndex => (prevIndex + 1) % placeholders.length);
        }, 4000);

        return () => clearInterval(interval);
    }, [placeholders.length]);

    return (
        <Box
            id="campaign-audition"
            sx={{
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: theme[themeStyle].wrapperColor,
                padding: '16px',
                borderRadius: {
                    xs: 0,
                    sm: '8px',
                },
                color: 'white',
                width: '100%',

                // maxWidth: '600px',
            }}>
            {/* Input Section */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '16px',
                }}>
                {/* <Avatar src="https://via.placeholder.com/150" alt="User Avatar" sx={{ width: 48, height: 48 }} /> */}
                <ThspianAvatar user={user!} />
                <Box
                    onClick={onOpenModal}
                    sx={{
                        position: 'relative',
                        width: '100%',
                        height: '56px', // Match TextField height
                        overflow: 'hidden',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}>
                    <TextField
                        variant="outlined"
                        fullWidth
                        InputProps={{
                            readOnly: true,
                            startAdornment: (
                                <Box
                                    sx={{
                                        position: 'relative',
                                        overflow: 'hidden',
                                        height: '100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        width: '100%',
                                    }}>
                                    {placeholders.map((placeholder, idx) => (
                                        <Typography
                                            key={idx}
                                            sx={{
                                                position: 'absolute',
                                                left: 0,
                                                top: '50%',
                                                transform: `translateY(${idx === currentIndex ? '-50%' : '-150%'})`,
                                                opacity: idx === currentIndex ? 1 : 0,
                                                transition: 'transform 0.6s ease-in-out, opacity 0.6s ease-in-out',
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                fontSize: 14,
                                                color: theme[themeStyle].darkGray60,
                                                pointerEvents: 'none',
                                                whiteSpace: 'nowrap',
                                            }}>
                                            {placeholder}
                                        </Typography>
                                    ))}
                                </Box>
                            ),
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: theme[themeStyle].zeus_dark,
                                borderRadius: '10px',
                                height: '56px',
                            },
                        }}
                    />
                </Box>
            </Box>

            <Divider variant="fullWidth" sx={{ color: theme[themeStyle].highlight, opacity: 0.5 }} />

            {/* Action Buttons */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-evenly',
                    marginTop: '8px',
                }}>
                <Button
                    startIcon={<JobIcon stroke={theme[themeStyle].highlight} height={'30px'} width={'30px'} />}
                    onClick={() => onButtonClick && onButtonClick('audition')}
                    sx={{
                        textTransform: 'none',
                        color: 'red',
                        fontWeight: 'bold',
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: {
                            xs: 12,
                            sm: 14,
                        },
                    }}>
                    Job/Gig
                </Button>
                {events && (
                    <Button
                        startIcon={
                            <CalendarColoredIcon
                                height={'30px'}
                                width={'30px'}
                                fill={theme[themeStyle].highlight}
                                stroke={theme[themeStyle].highlight}
                            />
                        }
                        onClick={() => onButtonClick && onButtonClick('event')}
                        sx={{
                            textTransform: 'none',
                            color: 'green',
                            fontWeight: 'bold',
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: {
                                xs: 12,
                                sm: 14,
                            },
                        }}>
                        Event
                    </Button>
                )}

                <Button
                    startIcon={<CampaignIcon height={'30px'} width={'30px'} />}
                    onClick={() => onButtonClick && onButtonClick('campaign')}
                    sx={{
                        textTransform: 'none',
                        color: theme[themeStyle].pink,
                        fontWeight: 'bold',
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: {
                            xs: 12,
                            sm: 14,
                        },
                    }}>
                    Campaign
                </Button>
            </Box>
        </Box>
    );
};

export default PostInput;
